import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { subHours } from 'date-fns'
import { useMemo } from 'react'

import {
  gpuAdminConfigured,
  gpuClient,
  type GpuSchemas,
  unwrapGpu,
} from '@/api/gpu/client'

export type GpuProvider = GpuSchemas['ProviderView']
export type GpuBalance = GpuSchemas['BalanceView']
export type GpuOffer = GpuSchemas['OfferView']
export type GpuHistoryEntry = GpuSchemas['HistoryView']
export type GpuSettings = GpuSchemas['SettingsView']
export type GpuStats = GpuSchemas['StatsView']
export type ProviderPatch = GpuSchemas['ProviderPatch']

export const GPU_KEY = ['gpu-admin'] as const

export const STATS_WINDOW_HOURS = 24

export function useGpuProviders() {
  return useQuery({
    queryKey: [...GPU_KEY, 'providers'],
    enabled: gpuAdminConfigured,
    queryFn: async () =>
      unwrapGpu(
        await gpuClient.GET('/gpu/admin/v1/providers'),
        'Failed to load GPU providers',
      ),
  })
}

export function useGpuProvider(key: string | undefined) {
  const query = useGpuProviders()
  const provider = query.data?.find(
    (p) => p.key.toLowerCase() === key?.toLowerCase(),
  )
  return { ...query, provider }
}

export function useGpuSettings() {
  return useQuery({
    queryKey: [...GPU_KEY, 'settings'],
    enabled: gpuAdminConfigured,
    queryFn: async () =>
      unwrapGpu(
        await gpuClient.GET('/gpu/admin/v1/settings'),
        'Failed to load GPU settings',
      ),
  })
}

export function useGpuStats(provider: string) {
  // One bound per mount, so refetches keep the same cohort.
  const since = useMemo(
    () => subHours(new Date(), STATS_WINDOW_HOURS).toISOString(),
    [],
  )
  return useQuery({
    queryKey: [...GPU_KEY, 'stats', provider, since],
    enabled: gpuAdminConfigured,
    queryFn: async () =>
      unwrapGpu(
        await gpuClient.GET('/gpu/admin/v1/providers/{provider}/stats', {
          params: { path: { provider }, query: { since } },
        }),
        'Failed to load provider stats',
      ),
  })
}

export interface PageArgs {
  limit: number
  offset: number
}

export function useGpuOffers(provider: string, page: PageArgs) {
  return useQuery({
    queryKey: [...GPU_KEY, 'offers', provider, page],
    enabled: gpuAdminConfigured,
    placeholderData: keepPreviousData,
    queryFn: async () =>
      unwrapGpu(
        await gpuClient.GET('/gpu/admin/v1/providers/{provider}/offers', {
          params: { path: { provider }, query: page },
        }),
        'Failed to load offers',
      ),
  })
}

// Without a provider: the global markup history.
export function useGpuHistory(provider: string | undefined, page: PageArgs) {
  return useQuery({
    queryKey: [...GPU_KEY, 'history', provider ?? 'global', page],
    enabled: gpuAdminConfigured,
    placeholderData: keepPreviousData,
    queryFn: async () =>
      unwrapGpu(
        provider
          ? await gpuClient.GET('/gpu/admin/v1/providers/{provider}/history', {
              params: { path: { provider }, query: page },
            })
          : await gpuClient.GET('/gpu/admin/v1/settings/history', {
              params: { query: page },
            }),
        'Failed to load settings history',
      ),
  })
}
